import { useEffect, useRef, useState } from "react";
import momMemoji from "../assets/mom-memoji.png" with { type: "file" };
import threadMemoji from "../assets/thread-memoji.png" with { type: "file" };

/**
 * The hero demo, built to read as a real iMessage screenshot rather than a
 * stylised mock: dark thread, status bar, nav row with avatar and back count,
 * bubbles with proper tails, a read receipt, and the compose bar.
 *
 * An ordinary conversation plays first and the mono payment arrives as the
 * natural next message.
 */
type Beat =
  | { kind: "text"; side: "in" | "out"; body: string; at: number }
  | { kind: "typing"; side: "in" | "out"; at: number; until: number }
  | { kind: "payment"; at: number }
  | { kind: "read"; at: number };

type Thread = {
  name: string;
  avatar: string;
  amount: string;
  readAt: string;
  beats: Beat[];
};

const THREADS = {
  mom: {
    name: "Mom",
    avatar: momMemoji,
    amount: "$200",
    readAt: "9:50 PM",
    beats: [
      { kind: "typing", side: "in",  at: 600,   until: 2200 },
      { kind: "text",   side: "in",  body: "hi sweetheart! how was your week?", at: 2200 },
      { kind: "text",   side: "out", body: "good! busy. how's the car holding up?", at: 4200 },
      { kind: "typing", side: "in",  at: 5400,  until: 7000 },
      { kind: "text",   side: "in",  body: "ah it needs a repair. dont you worry about it", at: 7000 },
      { kind: "payment", at: 9400 },
      { kind: "read", at: 10600 },
    ],
  },
  friend: {
    name: "Meek",
    avatar: threadMemoji,
    amount: "$55",
    readAt: "10:14 PM",
    beats: [
      { kind: "typing", side: "in",  at: 600,   until: 1900 },
      { kind: "text",   side: "in",  body: "yo when are you going to pay me back?", at: 1900 },
      { kind: "text",   side: "out", body: "yep sending now", at: 3800 },
      { kind: "payment", at: 6100 },
      { kind: "read", at: 7300 },
    ],
  },
} satisfies Record<string, Thread>;

const ORDER = ["mom", "friend"] as const;
const THREAD_DURATION = 12400;

export function PhoneDemo() {
  const [threadIndex, setThreadIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const data = THREADS[ORDER[threadIndex]];
  const [shown, setShown] = useState(0);
  const timers = useRef<number[]>([]);
  const audio = useRef<AudioContext | null>(null);

  const tone = (side: "in" | "out") => {
    audio.current ??= new AudioContext();
    const ctx = audio.current;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    const osc = ctx.createOscillator();
    const sweep = side === "out" ? [620, 980] : [880, 660];

    osc.type = "sine";
    osc.frequency.setValueAtTime(sweep[0], now);
    osc.frequency.exponentialRampToValueAtTime(sweep[1], now + 0.16);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(side === "out" ? 0.11 : 0.085, now + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.24);
  };

  const play = (side: "in" | "out") => {
    if (!soundEnabled) return;
    if ("vibrate" in navigator) {
      navigator.vibrate(side === "out" ? [10, 18, 8] : 12);
    }
    tone(side);
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(data.beats.length);
      return;
    }
    setShown(0);
    data.beats.forEach((b, i) => {
      timers.current.push(window.setTimeout(() => {
        setShown(i + 1);
        if (b.kind === "text") play(b.side);
        if (b.kind === "payment") play("out");
      }, b.at + 400));
    });
    timers.current.push(window.setTimeout(() => {
      setThreadIndex((current) => (current + 1) % ORDER.length);
    }, THREAD_DURATION));
    return () => {
      timers.current.forEach((t) => { window.clearTimeout(t); window.clearInterval(t); });
      timers.current = [];
    };
  }, [data.beats, soundEnabled]);

  return (
    <div className="imsg-demo">
      <button
        className={`imsg-sound ${soundEnabled ? "is-on" : ""}`}
        type="button"
        aria-label={soundEnabled ? "Turn message sounds off" : "Turn message sounds on"}
        aria-pressed={soundEnabled}
        onClick={() => {
          audio.current ??= new AudioContext();
          void audio.current.resume().then(() => {
            if (!soundEnabled) tone("out");
          });
          setSoundEnabled((enabled) => !enabled);
        }}
      >
        <SoundIcon muted={!soundEnabled} />
      </button>
      <div className="imsg-phone">
        <div className="imsg-screen">
        <div className="imsg-notch" />

        <div className="imsg-status">
          <span className="imsg-status__time">9:50</span>
          <span className="imsg-status__right">
            <Signal /> <Wifi /> <Battery />
          </span>
        </div>

        <div className="imsg-nav">
          <span className="imsg-nav__back">
            <Chevron />
            <span className="imsg-nav__count">126</span>
          </span>
          <span className="imsg-nav__who">
            <span
              className="imsg-avatar"
              aria-hidden="true"
              style={{ backgroundImage: `url(${data.avatar})` }}
            />
            <span className="imsg-nav__name">{data.name}&nbsp;›</span>
          </span>
          <span className="imsg-nav__facetime"><Video /></span>
        </div>

        <div className="imsg-thread">
          {data.beats.map((b, i) =>
            i >= shown ? null :
            b.kind === "typing" ? (
              shown === i + 1 ? (
                <div key={i} className={`imsg-row imsg-row--${b.side}`}>
                  <span className={`imsg-bubble imsg-bubble--${b.side} imsg-typing`}>
                    <i /><i /><i />
                  </span>
                </div>
              ) : null
            ) : b.kind === "text" ? (
              <div key={i} className={`imsg-row imsg-row--${b.side}`}>
                <span className={`imsg-bubble imsg-bubble--${b.side}`}>
                  {b.body}
                </span>
              </div>
            ) : b.kind === "payment" ? (
              <div key={i} className="imsg-row imsg-row--out">
                <span className="imsg-bubble imsg-bubble--out imsg-pay">
                  <span className="imsg-pay__logo" aria-hidden="true">$</span>
                  <span className="imsg-pay__amt">{data.amount}</span>
                  <span className="imsg-pay__meta">USDC sent with mono</span>
                </span>
              </div>
            ) : (
              <p key={i} className="imsg-read"><b>Read</b> {data.readAt}</p>
            ),
          )}
        </div>

          <div className="imsg-compose">
            <span className="imsg-compose__plus">+</span>
            <span className="imsg-compose__field">
              iMessage
              <span className="imsg-compose__wave"><Wave /></span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const SoundIcon = ({ muted }: { muted: boolean }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 9.5v5h3.2L12 18.3V5.7L7.2 9.5H4Z" fill="currentColor" />
    {muted ? (
      <path d="M16 9l4 4m0-4l-4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    ) : (
      <>
        <path d="M15.6 9.2a4.2 4.2 0 010 5.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18.2 6.7a7.6 7.6 0 010 10.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    )}
  </svg>
);

const Chevron = () => (
  <svg viewBox="0 0 24 24" className="imsg-ico" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Video = () => (
  <svg viewBox="0 0 24 24" className="imsg-ico" aria-hidden="true">
    <rect x="2.5" y="6.5" width="13" height="11" rx="3.2" fill="currentColor" />
    <path d="M17 11.2l4-2.6v6.8l-4-2.6z" fill="currentColor" />
  </svg>
);
const Signal = () => (
  <svg viewBox="0 0 20 12" className="imsg-sys" aria-hidden="true">
    {[0,1,2,3].map(i => (
      <rect key={i} x={i*5} y={9 - i*2.6} width="3.4" height={3 + i*2.6} rx="1"
            fill={i === 3 ? "rgba(255,255,255,.42)" : "currentColor"} />
    ))}
  </svg>
);
const Wifi = () => (
  <svg viewBox="0 0 18 13" className="imsg-sys" aria-hidden="true">
    <path d="M9 11.4l2.2-2.7a3.4 3.4 0 00-4.4 0L9 11.4z" fill="currentColor" />
    <path d="M2.4 4.6a10 10 0 0113.2 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <path d="M4.9 7.3a6.4 6.4 0 018.2 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);
const Battery = () => (
  <svg viewBox="0 0 27 13" className="imsg-sys imsg-sys--batt" aria-hidden="true">
    <rect x="0.6" y="0.6" width="22" height="11.8" rx="3.4" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="1.1" />
    <rect x="2.2" y="2.2" width="12" height="8.6" rx="2.2" fill="#F5C542" />
    <path d="M24.4 4.6v3.8a2.1 2.1 0 000-3.8z" fill="rgba(255,255,255,.4)" />
  </svg>
);
const Wave = () => (
  <svg viewBox="0 0 18 14" aria-hidden="true">
    {[3,6,2,7,4].map((h,i) => (
      <rect key={i} x={i*3.4+0.6} y={7-h/2} width="1.7" height={h} rx="0.85" fill="currentColor" />
    ))}
  </svg>
);
